/**
 * Аналітика цін — об'єднаний інтерфейс, 3 режими:
 *
 *   1) code     — за кодом УКТЗЕД (як стара "Аналітика цін")
 *   2) item     — за конкретною декларацією (anchor_id з пошуку)
 *   3) text     — за описом товару (свобідний текст)
 *
 * UI принцип: НЕ показувати P25/P75 без підказок. «дешевий край / типова /
 * дорогий край» + кольорова шкала.
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Search, X, Sparkles, Info, ArrowLeft, ArrowUp, ArrowDown, Hash, Type, ListChecks } from 'lucide-react';
import { analyticsSemanticPrice } from '../../services/api';
import HSCode from '../common/HSCode';

const fmtPrice = (v) => v == null ? '—' : `$${Number(v).toFixed(2)}`;
const fmtN = (v) => v == null ? '—' : Number(v).toLocaleString('uk-UA');

const MODES = [
  { id: 'code', label: 'За кодом УКТЗЕД',    Icon: Hash,     hint: 'Усі декларації з даним кодом — найшвидший спосіб подивитись ринок' },
  { id: 'item', label: 'За товаром',         Icon: Sparkles, hint: 'Семантично схожі на конкретну декларацію (вибрану з пошуку)' },
  { id: 'text', label: 'За описом',          Icon: Type,     hint: 'Введи опис товару — модель знайде схожі' },
];

export default function SemanticPriceTab({
  initialAnchorId = null,
  initialQuery = '',
  initialCodePrefix = '',
  onClose,
}) {
  // Початковий режим залежить від того, з чим прийшли:
  // - є anchorId → 'item'
  // - є query    → 'text'
  // - інакше     → 'code'
  const initialMode = initialAnchorId != null ? 'item'
                    : initialQuery ? 'text' : 'code';

  const [mode, setMode] = useState(initialMode);
  // anchorId зберігається ВЗАЕМО — навіть у code/text режимах,
  // бо вкладка «За товаром» має до нього повертатися без втрат.
  const [anchorId, setAnchorId] = useState(initialAnchorId);
  const [query, setQuery] = useState(initialQuery);
  const [codePrefix, setCodePrefix] = useState(initialCodePrefix);
  const [country, setCountry] = useState('');
  const [dateFrom, setDateFrom] = useState('2024-01-01');
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [minSim, setMinSim] = useState(0.6);
  const [yourPrice, setYourPrice] = useState('');

  // Окрема пам'ять результатів та помилок ПО КОЖНОМУ РЕЖИМУ,
  // щоб переключення вкладок не очищало вже завантажене.
  const [dataByMode, setDataByMode] = useState({ code: null, item: null, text: null });
  const [errorByMode, setErrorByMode] = useState({ code: '', item: '', text: '' });
  const [loadingMode, setLoadingMode] = useState(null);

  const data = dataByMode[mode];
  const error = errorByMode[mode];
  const loading = loadingMode === mode;

  // Перевіряємо чи зараз введені дані змінилися vs того, з чим востаннє
  // підтягнули результат — щоб показати «новий пошук потрібен» (можна).
  // Поки що просто завантажуємо явно через load().

  const load = useCallback(async (targetMode = mode) => {
    // Валідація вхідних даних залежно від режиму
    if (targetMode === 'code' && !codePrefix?.trim()) {
      setErrorByMode(p => ({ ...p, code: 'Введи код УКТЗЕД (хоча б 2 цифри)' }));
      return;
    }
    if (targetMode === 'item' && anchorId == null) {
      setErrorByMode(p => ({ ...p, item: 'Обери декларацію з пошуку (натисни «Ціни схожих» у картці)' }));
      return;
    }
    if (targetMode === 'text' && !query?.trim()) {
      setErrorByMode(p => ({ ...p, text: 'Введи опис товару' }));
      return;
    }
    setErrorByMode(p => ({ ...p, [targetMode]: '' }));
    setLoadingMode(targetMode);
    try {
      const r = await analyticsSemanticPrice({
        anchorId: targetMode === 'item' ? anchorId : undefined,
        query:    targetMode === 'text' ? query.trim() : undefined,
        codePrefix: codePrefix?.trim() || undefined,
        country: country?.trim() || undefined,
        dateFrom, dateTo,
        minSimilarity: targetMode === 'code' ? undefined : minSim,
        maxNeighbors: targetMode === 'code' ? 5000 : 1500,
      });
      setDataByMode(p => ({ ...p, [targetMode]: r }));
    } catch (e) {
      setErrorByMode(p => ({ ...p, [targetMode]: e?.message || 'Не вдалось завантажити аналітику' }));
      setDataByMode(p => ({ ...p, [targetMode]: null }));
    } finally {
      setLoadingMode(prev => prev === targetMode ? null : prev);
    }
  }, [mode, anchorId, query, codePrefix, country, dateFrom, dateTo, minSim]);

  // Auto-load на першому рендері для початкового режиму
  useEffect(() => { load(initialMode); }, []); // eslint-disable-line

  // ── Drill-down state ──────────────────────────────────────────────
  // Drill = «розкласти один бар гістограми по поличках».
  // Зберігаємо ПО РЕЖИМУ — якщо користувач відкрив drill у режимі code,
  // потім переключився на text і назад → повернемось до того самого drill.
  const [drillByMode, setDrillByMode] = useState({ code: null, item: null, text: null });
  const [drillDataByMode, setDrillDataByMode] = useState({ code: null, item: null, text: null });
  const [drillLoadingMode, setDrillLoadingMode] = useState(null);
  const [drillError, setDrillError] = useState('');

  const drillBin = drillByMode[mode];
  const drillData = drillDataByMode[mode];
  const drillLoading = drillLoadingMode === mode;

  const openDrill = useCallback(async (bin) => {
    setDrillByMode(p => ({ ...p, [mode]: bin }));
    setDrillError('');
    setDrillLoadingMode(mode);
    try {
      const r = await analyticsSemanticPrice({
        anchorId: mode === 'item' ? anchorId : undefined,
        query:    mode === 'text' ? query.trim() : undefined,
        codePrefix: codePrefix?.trim() || undefined,
        country: country?.trim() || undefined,
        dateFrom, dateTo,
        minSimilarity: mode === 'code' ? undefined : minSim,
        maxNeighbors: mode === 'code' ? 5000 : 1500,
        priceMin: bin.range_lo,
        priceMax: bin.range_hi,
      });
      setDrillDataByMode(p => ({ ...p, [mode]: r }));
    } catch (e) {
      setDrillError(e?.message || 'Не вдалось завантажити деталі коридору');
      setDrillDataByMode(p => ({ ...p, [mode]: null }));
    } finally {
      setDrillLoadingMode(prev => prev === mode ? null : prev);
    }
  }, [mode, anchorId, query, codePrefix, country, dateFrom, dateTo, minSim]);

  const closeDrill = useCallback(() => {
    setDrillByMode(p => ({ ...p, [mode]: null }));
    setDrillDataByMode(p => ({ ...p, [mode]: null }));
    setDrillError('');
  }, [mode]);

  const closeAllDrills = useCallback(() => {
    setDrillByMode({ code: null, item: null, text: null });
    setDrillDataByMode({ code: null, item: null, text: null });
    setDrillError('');
  }, []);

  // При перемиканні вкладки — якщо для цієї вкладки ще немає даних і
  // вхідні поля заповнені, автоматично запускаємо завантаження.
  const switchMode = (newMode) => {
    setMode(newMode);
    if (dataByMode[newMode] == null && loadingMode !== newMode) {
      // Перевіряємо чи можемо взагалі завантажити (інпути готові)
      const canLoad =
        (newMode === 'code' && codePrefix?.trim()) ||
        (newMode === 'item' && anchorId != null) ||
        (newMode === 'text' && query?.trim());
      if (canLoad) load(newMode);
    }
  };

  // Якщо змінилися загальні фільтри (country, дати, minSim) — інвалідуємо
  // кеш для всіх вкладок щоб юзер бачив актуальні дані після кліку «Показати».
  // Поки що не робимо автоматичної інвалідації — юзер сам тисне кнопку.

  const stats = data?.stats;
  const samples = data?.samples || [];
  const byTM = data?.by_trademark || [];
  const byImp = data?.by_importer || [];
  const byCountry = data?.by_country || [];
  const byCode = data?.by_code || [];
  const byMonth = data?.by_month || [];
  const byValuation = data?.by_valuation_method || [];

  // «Перевірити свою ціну» — рахуємо вердикт прямо на фронті з stats
  const yourPriceN = parseFloat(yourPrice);
  const showVerdict = !isNaN(yourPriceN) && yourPriceN > 0 && stats?.median;
  let verdict = null;
  if (showVerdict) {
    const ratio = yourPriceN / stats.median;
    if (ratio < 0.7)       verdict = { tone: 'green',  text: 'Нижче типової — добре', emoji: '🟢' };
    else if (ratio < 0.95) verdict = { tone: 'green',  text: 'Трохи нижче типової', emoji: '🟢' };
    else if (ratio < 1.15) verdict = { tone: 'gray',   text: 'У типовому коридорі', emoji: '⚪' };
    else if (ratio < 1.5)  verdict = { tone: 'yellow', text: 'Вище типової', emoji: '🟡' };
    else                   verdict = { tone: 'red',    text: 'Суттєво вище типової', emoji: '🔴' };
  }

  return (
    <div className="max-w-screen-2xl mx-auto p-4 space-y-4">
      {/* Header */}
      <div className="bg-surface rounded-xl shadow border border-border-default p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <h2 className="text-lg font-bold text-text-primary flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Аналітика цін
            </h2>
            <p className="text-xs text-text-muted mt-1">
              Один інструмент. Три способи подивитися ринок.
            </p>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-text-disabled hover:text-text-secondary">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Mode switch */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          {MODES.map(m => {
            const Icon = m.Icon;
            const isActive = mode === m.id;
            const isItemDisabled = m.id === 'item' && anchorId == null;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => !isItemDisabled && switchMode(m.id)}
                disabled={isItemDisabled}
                title={isItemDisabled ? 'Спочатку обери декларацію в основному пошуку' : m.hint}
                className={`flex flex-col items-start gap-1 p-3 rounded-lg border text-left transition-colors
                  ${isActive
                    ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-300 dark:border-blue-400 dark:bg-blue-950/40 dark:ring-blue-800'
                    : isItemDisabled
                      ? 'border-border-default bg-app text-text-disabled cursor-not-allowed'
                      : 'border-border-default bg-surface hover:border-blue-300 hover:bg-blue-50/30 dark:hover:border-blue-900/60 dark:hover:bg-blue-950/20'}
                `}>
                <div className="flex items-center gap-2 font-semibold text-sm">
                  <Icon className={`w-4 h-4 ${isActive ? 'text-blue-600 dark:text-blue-400' : ''}`} />
                  {m.label}
                </div>
                <div className="text-[11px] leading-snug text-text-muted">{m.hint}</div>
                {m.id === 'item' && anchorId != null && (
                  <div className="text-[10px] font-mono text-blue-700 dark:text-blue-300">декларація #{anchorId}</div>
                )}
              </button>
            );
          })}
        </div>

        {/* Mode-specific input */}
        {mode === 'code' && (
          <div className="bg-app border border-border-default rounded-lg p-3 mb-3">
            <label className="flex flex-col text-xs font-semibold text-text-secondary uppercase">
              Код УКТЗЕД (можна префікс — 2-10 цифр)
              <input value={codePrefix}
                onChange={e => setCodePrefix(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="напр. 8467 (всі шуруповерти/перфоратори) або 8467211000 (точно)"
                className="mt-1 border border-border-strong rounded px-3 py-2 text-base text-text-primary"
                inputMode="numeric" autoFocus />
              <span className="text-[11px] text-text-disabled mt-1">
                Прямий запит у БД, без семантики. Найшвидший варіант для перегляду ринку за кодом.
              </span>
            </label>
          </div>
        )}

        {mode === 'item' && anchorId != null && (
          <div className="bg-blue-50 border border-blue-200 dark:bg-blue-950/30 dark:border-blue-900/50 rounded-lg p-3 mb-3 flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-blue-900 dark:text-blue-200">
                Якір — декларація #{anchorId}
              </div>
              <div className="text-blue-700 dark:text-blue-300 text-xs mt-0.5">
                Береться векторне значення опису цього товару (як «Похожі 10» у картці).
              </div>
              {data?.anchor && data.anchor.description && (
                <div className="mt-2 text-text-secondary text-xs bg-surface/60 rounded px-2 py-1.5 border border-blue-100 dark:border-blue-900/40">
                  <span className="inline-flex items-center gap-2 align-middle flex-wrap">
                    <HSCode code={data.anchor.product_code} size="sm" showCopyAlways />
                    {data.anchor.trademark && <span>· ТМ <b>{data.anchor.trademark}</b></span>}
                    {data.anchor.country_of_origin && <span>· {data.anchor.country_of_origin}</span>}
                    {data.anchor.price_per_kg_usd != null && <span>· ваша ціна <b>{fmtPrice(data.anchor.price_per_kg_usd)}/кг</b></span>}
                  </span>
                  <div className="mt-0.5 line-clamp-2 text-text-secondary">{data.anchor.description}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {mode === 'text' && (
          <div className="bg-app border border-border-default rounded-lg p-3 mb-3">
            <label className="flex flex-col text-xs font-semibold text-text-secondary uppercase">
              Опис товару
              <input value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="напр. шуруповерт акумуляторний BOSCH 18V"
                className="mt-1 border border-border-strong rounded px-3 py-2 text-base text-text-primary"
                autoFocus />
              <span className="text-[11px] text-text-disabled mt-1">
                Модель закодує опис і знайде семантично схожі декларації. ~1-2 сек.
              </span>
            </label>
          </div>
        )}

        {/* Filters — общие для всех режимов */}
        <div className={`grid gap-3 items-end pt-2 border-t border-border-default ${
          mode === 'code'
            ? 'grid-cols-2 md:grid-cols-6'
            : 'grid-cols-2 md:grid-cols-7'
        }`}>
          {/* code-prefix фильтр для item/text режимов */}
          {mode !== 'code' && (
            <label className="flex flex-col text-xs font-semibold text-text-secondary uppercase">
              Код УКТЗЕД
              <input value={codePrefix}
                onChange={e => setCodePrefix(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="опц."
                className="mt-1 border border-border-strong rounded px-2 py-2 text-sm" inputMode="numeric" />
            </label>
          )}
          <label className="flex flex-col text-xs font-semibold text-text-secondary uppercase">
            Країна
            <input value={country}
              onChange={e => setCountry(e.target.value.toUpperCase())}
              placeholder="напр. КИТАЙ"
              className="mt-1 border border-border-strong rounded px-2 py-2 text-sm" />
          </label>
          <label className="flex flex-col text-xs font-semibold text-text-secondary uppercase">
            Дата від
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="mt-1 border border-border-strong rounded px-2 py-2 text-sm" />
          </label>
          <label className="flex flex-col text-xs font-semibold text-text-secondary uppercase">
            Дата до
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="mt-1 border border-border-strong rounded px-2 py-2 text-sm" />
          </label>
          {mode !== 'code' && (
            <label className="flex flex-col text-xs font-semibold text-text-secondary uppercase">
              Схожість
              <select value={minSim.toFixed(2)} onChange={e => setMinSim(parseFloat(e.target.value))}
                className="mt-1 border border-border-strong rounded px-2 py-2 text-sm">
                <option value="0.50">Широко 0.50</option>
                <option value="0.60">Помірно 0.60</option>
                <option value="0.70">Точно 0.70</option>
                <option value="0.80">Дуже близькі 0.80</option>
              </select>
            </label>
          )}
          <label className="flex flex-col text-xs font-semibold text-text-secondary uppercase">
            Перевірити ціну
            <input type="number" step="0.01" min="0" value={yourPrice}
              onChange={e => setYourPrice(e.target.value)}
              placeholder="$/кг"
              className="mt-1 border border-border-strong rounded px-2 py-2 text-sm" />
          </label>
          <button onClick={() => { closeAllDrills(); load(); }} disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-semibold px-4 py-2 rounded flex items-center justify-center gap-2 disabled:opacity-50">
            <Search className="w-4 h-4" /> {loading ? '…' : 'Показати'}
          </button>
        </div>

        {error && <div className="mt-2 text-red-600 dark:text-red-400 text-sm">{error}</div>}

        {/* Verdict для введённой "своей цены" */}
        {verdict && (
          <div className={`mt-3 rounded-lg px-3 py-2 text-sm font-semibold border
            ${verdict.tone === 'green'  ? 'bg-green-50  text-green-800  border-green-200  dark:bg-green-950/30  dark:text-green-300  dark:border-green-900/50' :
              verdict.tone === 'gray'   ? 'bg-app       text-text-primary border-border-default' :
              verdict.tone === 'yellow' ? 'bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-900/50' :
                                          'bg-red-50    text-red-800    border-red-200    dark:bg-red-950/30    dark:text-red-300    dark:border-red-900/50'}`}>
            {verdict.emoji} Ваша ціна <b>${yourPriceN.toFixed(2)}/кг</b> — {verdict.text}
            <span className="text-xs font-normal opacity-75 ml-2">
              (типова {fmtPrice(stats.median)}/кг, відхилення {((yourPriceN/stats.median - 1) * 100).toFixed(0)}%)
            </span>
          </div>
        )}
      </div>

      {/* Empty / no results */}
      {data && data.n_with_price === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 dark:bg-yellow-950/30 dark:border-yellow-900/50 rounded-xl p-4 text-sm text-yellow-800 dark:text-yellow-300">
          Знайдено {fmtN(data.n_neighbors)} схожих товарів, але <b>жоден не має заповненої ціни</b>.
          Це переважно дані до 2024 року. Розширте діапазон дат або зменшіть «наскільки схожі».
        </div>
      )}

      {/* Main: simple stats card */}
      {stats && data.n_with_price > 0 && (
        <SimplePriceCard stats={stats} n={data.n_with_price} anchorPrice={data.anchor?.price_per_kg_usd} />
      )}

      {/* Visual histogram + drill-down */}
      {data?.histogram?.length > 0 && stats && (
        <PriceHistogramSimple
          data={data}
          stats={stats}
          anchorPrice={data.anchor?.price_per_kg_usd}
          onBinClick={openDrill}
          activeBinIdx={drillBin
            ? data.histogram.findIndex(b =>
                b.range_lo === drillBin.range_lo && b.range_hi === drillBin.range_hi)
            : null}
        />
      )}

      {/* Drill panel — деталі обраного цінового коридору */}
      {drillBin && (
        <DrillPanel
          bin={drillBin}
          data={drillData}
          loading={drillLoading}
          error={drillError}
          onClose={closeDrill}
        />
      )}

      {/* Slices grid */}
      {data && data.n_with_price > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {byTM.length > 0 && <SliceCard title="За торговою маркою (брендом)" rows={byTM}
            keyCol="trademark" />}
          {byCountry.length > 0 && <SliceCard title="За країною походження" rows={byCountry}
            keyCol="country" />}
          {byImp.length > 0 && <SliceCard title="Хто більше всіх ввозив" rows={byImp.slice(0, 15)}
            keyCol="importer_name"
            keyFmt={(_, row) => <ImporterCell row={row} />}
            extraCol="value_total_usd" extraLabel="Сума, $" />}
          {byCode.length > 0 && <SliceCard title="За кодом УКТЗЕД (підкатегорії)" rows={byCode}
            keyCol="product_code"
            keyFmt={(v) => <HSCode code={v} size="sm" />} />}
          {byValuation.length > 0 && <ValuationMethodCard rows={byValuation} totalN={data.n_with_price} />}
        </div>
      )}

      {/* Monthly trend simplified */}
      {byMonth.length > 0 && <MonthlyTrend rows={byMonth} />}

      {/* Top samples */}
      {samples.length > 0 && <SamplesTable rows={samples} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Главная плашка — простыми словами
// ─────────────────────────────────────────────────────────────────────
function SimplePriceCard({ stats, n, anchorPrice }) {
  const cheap = stats.p25;
  const typical = stats.median;
  const expensive = stats.p75;
  const min = stats.min;
  const max = stats.max;

  // оценка где находится якорь
  let anchorVerdict = null;
  if (anchorPrice && typical) {
    const ratio = anchorPrice / typical;
    if (ratio < 0.7)       anchorVerdict = { tone: 'green',  text: 'Ваша ціна нижче типової', emoji: '🟢' };
    else if (ratio < 0.95) anchorVerdict = { tone: 'green',  text: 'Ваша ціна трохи нижче типової', emoji: '🟢' };
    else if (ratio < 1.15) anchorVerdict = { tone: 'gray',   text: 'Ваша ціна — у типовому коридорі', emoji: '⚪' };
    else if (ratio < 1.5)  anchorVerdict = { tone: 'yellow', text: 'Ваша ціна вище типової', emoji: '🟡' };
    else                   anchorVerdict = { tone: 'red',    text: 'Ваша ціна суттєво вище типової', emoji: '🔴' };
  }

  return (
    <div className="bg-surface rounded-xl shadow border border-border-default p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-text-primary">Реальні ціни схожих товарів</h3>
        <span className="text-xs text-text-muted">
          На основі <b>{fmtN(n)}</b> декларацій
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <BigStat
          label="Найдешевша"
          value={fmtPrice(min)}
          sub="за кг"
          tone="gray"
          tooltip="Найнижча ціна за кг серед знайдених декларацій"
        />
        <BigStat
          label="Дешевий край"
          value={fmtPrice(cheap)}
          sub="за кг"
          tone="green"
          tooltip="25% сделок дешевше цієї цифри. Це нижня межа «нормального» коридору."
        />
        <BigStat
          label="ТИПОВА ЦІНА"
          value={fmtPrice(typical)}
          sub="за кг"
          tone="blue"
          highlight
          tooltip="Медіана — рівно посередині. Половина декларацій дешевше, половина дорожче."
        />
        <BigStat
          label="Дорогий край"
          value={fmtPrice(expensive)}
          sub="за кг"
          tone="orange"
          tooltip="25% сделок дорожче цієї цифри. Це верхня межа «нормального» коридору."
        />
        <BigStat
          label="Найдорожча"
          value={fmtPrice(max)}
          sub="за кг"
          tone="gray"
          tooltip="Найвища ціна за кг серед знайдених декларацій (можуть бути виброси)"
        />
      </div>

      {/* Visual range bar */}
      <PriceRangeBar min={min} cheap={cheap} typical={typical} expensive={expensive} max={max} anchor={anchorPrice} />

      {/* Anchor verdict */}
      {anchorVerdict && (
        <div className={`mt-4 rounded-lg px-3 py-2 text-sm font-semibold border
          ${anchorVerdict.tone === 'green'  ? 'bg-green-50  text-green-800  border-green-200  dark:bg-green-950/30  dark:text-green-300  dark:border-green-900/50' :
            anchorVerdict.tone === 'gray'   ? 'bg-app       text-text-primary border-border-default' :
            anchorVerdict.tone === 'yellow' ? 'bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-900/50' :
                                              'bg-red-50    text-red-800    border-red-200    dark:bg-red-950/30    dark:text-red-300    dark:border-red-900/50'}`}>
          {anchorVerdict.emoji} {anchorVerdict.text} ({fmtPrice(anchorPrice)}/кг при типовій {fmtPrice(typical)}/кг)
        </div>
      )}
    </div>
  );
}

function BigStat({ label, value, sub, tone, highlight, tooltip }) {
  const colors = {
    gray:   'bg-app border-border-default text-text-primary',
    green:  'bg-green-50 border-green-200 text-green-900 dark:bg-green-950/30 dark:border-green-900/50 dark:text-green-300',
    blue:   'bg-blue-100 border-blue-300 text-blue-900 dark:bg-blue-950/40 dark:border-blue-900/60 dark:text-blue-300',
    orange: 'bg-orange-50 border-orange-200 text-orange-900 dark:bg-orange-950/30 dark:border-orange-900/50 dark:text-orange-300',
  };
  return (
    <div className={`rounded-lg border p-3 relative ${colors[tone] || colors.gray}
                     ${highlight ? 'ring-2 ring-blue-400 shadow-md' : ''}`}>
      <div className="flex items-center gap-1 text-[11px] uppercase font-semibold opacity-70">
        {label}
        {tooltip && (
          <span title={tooltip} className="cursor-help">
            <Info className="w-3 h-3 opacity-60" />
          </span>
        )}
      </div>
      <div className={`text-xl font-bold mt-0.5 ${highlight ? 'text-blue-700 dark:text-blue-300' : ''}`}>{value}</div>
      <div className="text-[11px] opacity-60">{sub}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Визуальная шкала «дёшево—типично—дорого» с маркером твоей цены
// ─────────────────────────────────────────────────────────────────────
function PriceRangeBar({ min, cheap, typical, expensive, max, anchor }) {
  if (!min || !max || max <= min) return null;
  const range = max - min;
  const pos = (v) => v == null ? null : Math.max(0, Math.min(100, ((v - min) / range) * 100));

  const cheapPct = pos(cheap);
  const typicalPct = pos(typical);
  const expensivePct = pos(expensive);
  const anchorPct = pos(anchor);

  return (
    <div className="mt-4 px-1">
      <div className="text-[11px] text-text-muted mb-1 flex justify-between">
        <span>{fmtPrice(min)}</span>
        <span className="text-text-disabled">типова → {fmtPrice(typical)}/кг</span>
        <span>{fmtPrice(max)}</span>
      </div>
      <div className="relative h-3 rounded-full overflow-hidden bg-app dark:bg-slate-700">
        {/* Cheap zone */}
        <div className="absolute h-full" style={{ left: 0, width: `${cheapPct}%`, background: 'var(--chart-cheap)' }} />
        {/* Normal zone */}
        <div className="absolute h-full" style={{ left: `${cheapPct}%`, width: `${expensivePct - cheapPct}%`, background: 'var(--chart-typical)' }} />
        {/* Expensive zone */}
        <div className="absolute h-full" style={{ left: `${expensivePct}%`, width: `${100 - expensivePct}%`, background: 'var(--chart-expensive)' }} />
        {/* Median tick */}
        <div className="absolute top-0 h-full w-0.5" style={{ left: `${typicalPct}%`, background: 'var(--chart-typical-stroke)' }} title={`Типова: ${fmtPrice(typical)}/кг`} />
        {/* Anchor tick */}
        {anchorPct != null && (
          <div className="absolute -top-1 h-5 w-1 bg-red-600 dark:bg-red-500 rounded shadow"
               style={{ left: `${anchorPct}%` }}
               title={`Ваша ціна: ${fmtPrice(anchor)}/кг`} />
        )}
      </div>
      <div className="text-[11px] text-text-muted mt-1 flex justify-between">
        <span>← дешево</span>
        <span>типовий коридор</span>
        <span>дорого →</span>
      </div>
      {anchorPct != null && (
        <div className="text-xs text-red-700 dark:text-red-400 mt-1 font-semibold">
          ▲ Червона мітка — ваша ціна {fmtPrice(anchor)}/кг
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Гистограмма (упрощённо)
// ─────────────────────────────────────────────────────────────────────
// "красивый" max — ceil to 1, 2, 5, 10, 20, 50…
function niceCeil(n) {
  if (n <= 5) return Math.max(1, Math.ceil(n));
  const mag = Math.pow(10, Math.floor(Math.log10(n)));
  const norm = n / mag;
  if (norm <= 1) return mag;
  if (norm <= 2) return 2 * mag;
  if (norm <= 5) return 5 * mag;
  return 10 * mag;
}

function PriceHistogramSimple({ data, stats, anchorPrice, onBinClick, activeBinIdx = null }) {
  const [hover, setHover] = useState(null);
  const bins = data.histogram || [];
  if (bins.length === 0) return null;

  const maxCnt = Math.max(...bins.map(b => b.count));
  const lo = bins[0].range_lo;
  const hi = bins[bins.length - 1].range_hi;
  const range = hi - lo || 1;

  const colorOf = (b) => {
    const mid = (b.range_lo + b.range_hi) / 2;
    if (stats.p25 != null && mid <  stats.p25) return { fill: 'var(--chart-cheap)',     stroke: 'var(--chart-cheap-stroke)',     label: 'дешевше' };
    if (stats.p75 != null && mid >  stats.p75) return { fill: 'var(--chart-expensive)', stroke: 'var(--chart-expensive-stroke)', label: 'дорожче' };
    return                                           { fill: 'var(--chart-typical)',   stroke: 'var(--chart-typical-stroke)',   label: 'типовий' };
  };

  // Aspect 1000:320 → ratio 25:8. Контейнер задаёт пропорцию, SVG сохраняет.
  const W = 1000, H = 320;
  const PAD = { L: 56, R: 28, T: 30, B: 56 };
  const innerW = W - PAD.L - PAD.R;
  const innerH = H - PAD.T - PAD.B;
  const gap = 3;
  const barW = (innerW - (bins.length - 1) * gap) / bins.length;

  const niceMax = niceCeil(maxCnt);
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(t => Math.round(niceMax * t));
  const yOf = (cnt) => PAD.T + innerH - (cnt / niceMax) * innerH;

  const xTickN = 6;
  const xTicks = Array.from({ length: xTickN }, (_, i) => {
    const v = lo + (range * i) / (xTickN - 1);
    return { val: v, x: PAD.L + (i / (xTickN - 1)) * innerW };
  });
  const xOf = (p) => PAD.L + ((p - lo) / range) * innerW;

  // Какой бин содержит медиану — подсветим его рамкой
  const medianBinIdx = stats.median == null ? -1 :
    bins.findIndex(b => stats.median >= b.range_lo && stats.median <= b.range_hi);

  return (
    <div className="bg-surface rounded-xl shadow-sm border border-border-default p-5">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="font-bold text-text-primary text-base">Розподіл цін</h3>
        <span className="text-[11px] text-text-disabled tracking-wide uppercase">скільки декларацій по коридорах ціни</span>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mb-3 text-[12px]">
        <LegendChip swatchVar="--chart-cheap" strokeVar="--chart-cheap-stroke" label="дешевше типового" />
        <LegendChip swatchVar="--chart-typical" strokeVar="--chart-typical-stroke" label="типовий коридор" />
        <LegendChip swatchVar="--chart-expensive" strokeVar="--chart-expensive-stroke" label="дорожче типового" />
        <span className="h-4 w-px bg-border-default" />
        <span className="inline-flex items-center gap-1.5 text-text-secondary">
          <svg width="22" height="10"><line x1="1" y1="5" x2="21" y2="5" stroke="#1d4ed8" strokeWidth="2" strokeDasharray="4,3"/></svg>
          типова ціна <b>{fmtPrice(stats.median)}/кг</b>
        </span>
        {anchorPrice != null && (
          <span className="inline-flex items-center gap-1.5 text-text-secondary">
            <svg width="14" height="14"><line x1="7" y1="1" x2="7" y2="13" stroke="#dc2626" strokeWidth="2.5"/></svg>
            ваша ціна <b className="text-red-700 dark:text-red-400">{fmtPrice(anchorPrice)}/кг</b>
          </span>
        )}
      </div>

      {/* Chart container with fixed aspect ratio */}
      <div className="w-full" style={{ aspectRatio: `${W}/${H}` }}>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full"
             preserveAspectRatio="xMidYMid meet">

          {/* Y grid */}
          {yTicks.map((cnt, i) => (
            <g key={`y${i}`}>
              <line x1={PAD.L} x2={PAD.L + innerW} y1={yOf(cnt)} y2={yOf(cnt)}
                    stroke={i === 0 ? 'var(--chart-axis)' : 'var(--chart-grid)'} strokeWidth="1" />
              <text x={PAD.L - 10} y={yOf(cnt) + 4} textAnchor="end"
                    fontSize="11" fill="var(--chart-axis-text)" fontFamily="ui-sans-serif">
                {cnt}
              </text>
            </g>
          ))}

          {/* Bars */}
          {bins.map((b, i) => {
            const h = (b.count / niceMax) * innerH;
            const x = PAD.L + i * (barW + gap);
            const y = PAD.T + (innerH - h);
            const c = colorOf(b);
            const isHover = hover === i;
            const isMedianBin = i === medianBinIdx;
            const isActive = activeBinIdx === i;
            const handleClick = onBinClick ? () => onBinClick(b, i) : undefined;
            return (
              <g key={i}
                 onMouseEnter={() => setHover(i)}
                 onMouseLeave={() => setHover(null)}
                 onClick={handleClick}
                 style={{ cursor: onBinClick ? 'pointer' : 'default' }}>
                {/* hit area */}
                <rect x={x - gap/2} y={PAD.T} width={barW + gap} height={innerH} fill="transparent" />
                {/* the bar */}
                <rect x={x} y={y} width={barW} height={h}
                      fill={c.fill}
                      stroke={isActive ? '#dc2626' : (isHover || isMedianBin ? c.stroke : 'none')}
                      strokeWidth={isActive ? 3 : isHover ? 2 : isMedianBin ? 1.5 : 0}
                      rx="2" ry="2"
                      style={{ transition: 'all 120ms ease',
                               filter: isActive ? 'drop-shadow(0 0 4px rgba(220,38,38,0.5))' : undefined }} />
                {/* count above bar — for prominent ones */}
                {b.count >= niceMax * 0.4 && (
                  <text x={x + barW/2} y={y - 5} textAnchor="middle"
                        fontSize="11" fontWeight="600"
                        fill={isActive ? '#dc2626' : 'var(--chart-bar-count)'}
                        fontFamily="ui-sans-serif">{b.count}</text>
                )}
              </g>
            );
          })}

          {/* X-axis baseline */}
          <line x1={PAD.L} x2={PAD.L + innerW} y1={PAD.T + innerH} y2={PAD.T + innerH}
                stroke="var(--chart-axis-baseline)" strokeWidth="1" />

          {/* X ticks */}
          {xTicks.map((t, i) => (
            <g key={`x${i}`}>
              <line x1={t.x} x2={t.x}
                    y1={PAD.T + innerH} y2={PAD.T + innerH + 5}
                    stroke="var(--chart-axis)" strokeWidth="1" />
              <text x={t.x} y={PAD.T + innerH + 19} textAnchor="middle"
                    fontSize="11" fill="var(--chart-axis-text)" fontFamily="ui-sans-serif">
                ${t.val < 10 ? t.val.toFixed(2) : t.val < 100 ? t.val.toFixed(1) : Math.round(t.val)}
              </text>
            </g>
          ))}

          {/* Median dashed line + small badge at top */}
          {stats.median != null && stats.median >= lo && stats.median <= hi && (
            <g>
              <line x1={xOf(stats.median)} x2={xOf(stats.median)}
                    y1={PAD.T - 6} y2={PAD.T + innerH}
                    stroke="var(--chart-typical-stroke)" strokeWidth="2" strokeDasharray="4,3" />
              <g transform={`translate(${xOf(stats.median)}, ${PAD.T - 14})`}>
                <rect x="-22" y="-9" width="44" height="14" rx="3" fill="var(--chart-typical-stroke)" />
                <text x="0" y="1" textAnchor="middle" fontSize="10"
                      fill="white" fontWeight="600">типова</text>
              </g>
            </g>
          )}

          {/* Anchor solid red line */}
          {anchorPrice != null && anchorPrice >= lo && anchorPrice <= hi && (
            <g>
              <line x1={xOf(anchorPrice)} x2={xOf(anchorPrice)}
                    y1={PAD.T - 6} y2={PAD.T + innerH + 4}
                    stroke="#dc2626" strokeWidth="2.5" />
              <polygon
                points={`${xOf(anchorPrice)},${PAD.T - 8}
                         ${xOf(anchorPrice) - 5},${PAD.T - 16}
                         ${xOf(anchorPrice) + 5},${PAD.T - 16}`}
                fill="#dc2626" />
            </g>
          )}

          {/* Axis labels */}
          <text x={14} y={PAD.T + innerH/2}
                textAnchor="middle" fontSize="11" fill="var(--chart-axis-text)"
                transform={`rotate(-90, 14, ${PAD.T + innerH/2})`}>
            кількість декларацій
          </text>
          <text x={PAD.L + innerW/2} y={H - 8} textAnchor="middle"
                fontSize="11" fill="var(--chart-axis-text)">
            ціна, $/кг
          </text>
        </svg>
      </div>

      {/* Hover details — fixed area below chart */}
      <div className="mt-3 min-h-[28px] flex items-center text-sm">
        {hover != null && bins[hover] ? (
          <div className="flex items-center gap-3 text-text-secondary">
            <span className="inline-block w-3 h-3 rounded"
                  style={{ background: colorOf(bins[hover]).fill, border: `1.5px solid ${colorOf(bins[hover]).stroke}` }} />
            <span className="font-mono text-text-primary">
              ${bins[hover].range_lo.toFixed(2)}–${bins[hover].range_hi.toFixed(2)}/кг
            </span>
            <span className="text-text-disabled">·</span>
            <b>{bins[hover].count} декларацій</b>
            <span className="text-text-disabled">·</span>
            <span className="text-xs text-text-muted">{colorOf(bins[hover]).label} коридор</span>
            {onBinClick && <span className="text-xs text-blue-600 ml-2">→ натисни щоб побачити деталі</span>}
          </div>
        ) : (
          <span className="text-xs text-text-disabled italic">
            {onBinClick
              ? 'наведи курсор на стовпчик і натисни щоб побачити деталі коридору'
              : 'наведи курсор на стовпчик щоб побачити точний діапазон і кількість'}
          </span>
        )}
      </div>
    </div>
  );
}

function LegendChip({ color, swatchVar, strokeVar, label }) {
  const style = swatchVar
    ? { background: `var(${swatchVar})`, borderColor: `var(${strokeVar})` }
    : undefined;
  return (
    <span className="inline-flex items-center gap-1.5 text-text-secondary">
      <span className={`w-3 h-3 rounded border ${color || ''}`} style={style} />
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Универсальная плашка-разрез — с визуальными шкалами и сортировкой
// ─────────────────────────────────────────────────────────────────────
function SliceCard({ title, rows, keyCol, keyFmt, extraCol, extraLabel }) {
  const keyLabel = title.includes('УКТЗЕД') ? 'Код' :
                   title.includes('брендом') ? 'Бренд' :
                   title.includes('країною') ? 'Країна' :
                   title.includes('ввозив') ? 'Імпортер' : 'Назва';

  // Сортировка: default — по count desc (как пришло с бэка)
  const [sortBy, setSortBy] = useState('n');
  const [sortDir, setSortDir] = useState('desc'); // asc | desc

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const va = a[sortBy]; const vb = b[sortBy];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const dir = sortDir === 'asc' ? 1 : -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
    return copy;
  }, [rows, sortBy, sortDir]);

  const onSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  // Для шкал — вычисляем максимумы (по всем строкам, не только видимым)
  const maxN = Math.max(...rows.map(r => r.n || 0));
  const priceVals = rows.map(r => r.price_median).filter(v => v != null);
  const maxPrice = priceVals.length ? Math.max(...priceVals) : 0;
  const minPrice = priceVals.length ? Math.min(...priceVals) : 0;
  const maxExtra = extraCol ? Math.max(...rows.map(r => r[extraCol] || 0)) : 0;

  return (
    <div className="bg-surface rounded-xl shadow border border-border-default p-4">
      <h3 className="font-semibold text-text-primary mb-2">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-text-muted uppercase border-b select-none">
            <tr>
              <SortHeader label={keyLabel} col={keyCol} sortBy={sortBy} sortDir={sortDir}
                          onClick={onSort} align="left" />
              <SortHeader label="Декл." col="n" sortBy={sortBy} sortDir={sortDir}
                          onClick={onSort} align="right" />
              <SortHeader label="Типова, $/кг" col="price_median" sortBy={sortBy} sortDir={sortDir}
                          onClick={onSort} align="right"
                          tooltip="Половина декларацій нижче, половина вище. Найреалістичніша ціна." />
              {extraCol && (
                <SortHeader label={extraLabel} col={extraCol} sortBy={sortBy} sortDir={sortDir}
                            onClick={onSort} align="right" />
              )}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => {
              const nPct = maxN > 0 ? (r.n / maxN) * 100 : 0;
              const pricePct = maxPrice > 0 ? (r.price_median / maxPrice) * 100 : 0;
              const isExpensive = r.price_median != null && minPrice > 0 &&
                                   r.price_median / minPrice >= 1.5;
              const isCheap = r.price_median != null && maxPrice > 0 &&
                               r.price_median / maxPrice <= 0.5;
              const extraPct = (extraCol && maxExtra > 0)
                ? ((r[extraCol] || 0) / maxExtra) * 100 : 0;
              return (
                <tr key={i} className="border-b border-border-default hover:bg-hover">
                  <td className="py-2 font-medium">
                    {keyFmt ? keyFmt(r[keyCol], r) : (r[keyCol] || '—')}
                  </td>
                  {/* Кол-во деклараций — голубая шкала */}
                  <td className="text-right">
                    <BarCell value={fmtN(r.n)} pct={nPct} barColor="bg-blue-200 dark:bg-blue-900/60" />
                  </td>
                  {/* Цена — цветная по тону */}
                  <td className="text-right">
                    <BarCell
                      value={fmtPrice(r.price_median)}
                      pct={pricePct}
                      barColor={isCheap
                        ? 'bg-green-200 dark:bg-green-900/60'
                        : isExpensive
                          ? 'bg-orange-200 dark:bg-orange-900/60'
                          : 'bg-blue-100 dark:bg-blue-950/50'}
                      bold
                    />
                  </td>
                  {extraCol && (
                    <td className="text-right">
                      <BarCell value={fmtN(r[extraCol])} pct={extraPct} barColor="bg-purple-200 dark:bg-purple-900/60" />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-text-disabled mt-2">
        Натисни на заголовок колонки щоб відсортувати. Шкала за рядком — пропорція від найбільшого значення.
      </div>
    </div>
  );
}

function SortHeader({ label, col, sortBy, sortDir, onClick, align = 'left', tooltip }) {
  const active = sortBy === col;
  return (
    <th
      onClick={() => onClick(col)}
      title={tooltip}
      className={`py-2 px-1 cursor-pointer hover:text-text-primary ${align === 'left' ? 'text-left' : 'text-right'}`}
    >
      <span className={`inline-flex items-center gap-0.5 ${align === 'right' ? 'flex-row-reverse' : ''} ${active ? 'text-blue-700 dark:text-blue-300 font-bold' : ''}`}>
        {label}
        {active
          ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
          : <span className="opacity-30 w-3">↕</span>}
      </span>
    </th>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Метод визначення митної вартості (1-6 за МКУ)
// ─────────────────────────────────────────────────────────────────────
const VALUATION_METHODS = {
  0: { label: 'не вказано',  short: '—',  hint: 'Метод не зазначено в декларації' },
  1: { label: 'основний (договірна ціна)',
       short: '1',
       hint: 'Митна вартість визначена на підставі ціни, фактично сплаченої або такої, що підлягає сплаті за товари (контракт). Найчастіше використовується для прозорих угод.' },
  2: { label: 'за ідентичними товарами',
       short: '2',
       hint: 'Якщо ціну за договором не можна підтвердити, береться ціна ідентичних товарів від того ж виробника (інша угода).' },
  3: { label: 'за подібними товарами',
       short: '3',
       hint: 'Як 2, але товари не повністю ідентичні, а лише схожі за призначенням і якістю.' },
  4: { label: 'віднімання вартості',
       short: '4',
       hint: 'Виходять з ціни товару на ринку України, потім віднімають націнку посередника, доставку, мито.' },
  5: { label: 'додавання вартості',
       short: '5',
       hint: 'Складають собівартість виробництва, прибуток виробника, доставку.' },
  6: { label: 'резервний',
       short: '6',
       hint: 'Митниця не змогла визначити вартість методами 1-5 і застосовує "розумний" підхід (часто завищує). Ознака суперечки.' },
};

const tone = {
  1: 'bg-green-50 text-green-800 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900/50',
  2: 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900/50',
  3: 'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900/50',
  4: 'bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-900/50',
  5: 'bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-900/50',
  6: 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900/50',
  0: 'bg-app text-text-secondary border-border-default',
};

function MethodBadge({ method, size = 'sm' }) {
  const m = VALUATION_METHODS[method] || VALUATION_METHODS[0];
  const cls = size === 'md'
    ? 'text-sm px-2 py-0.5'
    : 'text-[11px] px-1.5 py-0.5';
  return (
    <span className={`inline-flex items-center font-semibold rounded border ${cls} ${tone[method] || tone[0]}`}
          title={`Метод ${m.short}: ${m.label}\n\n${m.hint}`}>
      {m.short}
    </span>
  );
}

function ValuationMethodCard({ rows, totalN }) {
  const m6Row = rows.find(r => r.method === 6);
  const m6Pct = m6Row ? (m6Row.n / totalN * 100) : 0;

  return (
    <div className="bg-surface rounded-xl shadow border border-border-default p-4">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="font-semibold text-text-primary">За методом визначення митної вартості</h3>
        {m6Pct > 5 && (
          <span className="text-[11px] px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900/50" title="Резервний метод (6) — митниця коригувала вартість. Висока частка = підвищений ризик">
            ⚠ {m6Pct.toFixed(0)}% метод 6
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-text-muted uppercase border-b">
            <tr>
              <th className="text-left py-2">Метод</th>
              <th className="text-left py-2">Назва</th>
              <th className="text-right py-2">Декларацій</th>
              <th className="text-right py-2">Частка</th>
              <th className="text-right py-2">Типова, $/кг</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const m = VALUATION_METHODS[r.method] || VALUATION_METHODS[0];
              const pct = (r.n / totalN) * 100;
              return (
                <tr key={r.method} className="border-b border-border-default hover:bg-hover">
                  <td className="py-2"><MethodBadge method={r.method} size="md" /></td>
                  <td className="text-text-secondary" title={m.hint}>{m.label}</td>
                  <td className="text-right">
                    <BarCell value={fmtN(r.n)} pct={pct} barColor={r.method === 6 ? 'bg-red-200 dark:bg-red-900/60' : 'bg-blue-200 dark:bg-blue-900/60'} />
                  </td>
                  <td className="text-right text-text-secondary">{pct.toFixed(1)}%</td>
                  <td className="text-right font-semibold">{fmtPrice(r.price_median)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-text-muted mt-2">
        💡 Метод <b>1</b> — найчастіший і прозорий (договірна ціна).
        Метод <b>6</b> — митниця сумнівається у вартості, є коригування.
        Високий % методу 6 — підвищений ризик нової перевірки для подібних товарів.
      </div>
    </div>
  );
}

// Парсинг "ТОВ ИМЯ 49127 ОБЛАСТЬ ГОРОД УЛ ..." на (имя, адрес).
// Адрес обычно начинается с почтового индекса (5 цифр).
function parseImporter(fullName) {
  if (!fullName) return { name: '—', addr: '' };
  // Берём всё до первого 5-значного индекса (для UA), остаток как адрес
  const match = fullName.match(/^(.*?)\s*(\d{5}\s+.*)$/);
  if (match) {
    return { name: match[1].trim(), addr: match[2].trim() };
  }
  return { name: fullName.trim(), addr: '' };
}

// Двух-этажная ячейка для импортёра: жирное имя + мелкий адрес + ЄДРПОУ
function ImporterCell({ row }) {
  const { name, addr } = parseImporter(row.importer_name);
  return (
    <div className="leading-tight">
      <div className="font-semibold text-text-primary" title={row.importer_name}>
        {name}
      </div>
      {addr && (
        <div className="text-[11px] text-text-muted mt-0.5" title={addr}>
          {addr.length > 80 ? addr.slice(0, 80) + '…' : addr}
        </div>
      )}
      {row.importer_code != null && (
        <div className="text-[11px] text-text-disabled mt-0.5 font-mono">
          ЄДРПОУ {row.importer_code}
        </div>
      )}
    </div>
  );
}

// Ячейка с фоновой шкалой пропорции — справа цифра, слева растёт цветной бар
function BarCell({ value, pct, barColor = 'bg-blue-200', bold = false }) {
  return (
    <div className="relative inline-block w-full min-w-[80px]">
      <div className="absolute inset-y-0 left-0 rounded-sm opacity-60"
           style={{ width: `${Math.max(2, pct)}%` }}>
        <div className={`h-full ${barColor} rounded-sm`} />
      </div>
      <div className={`relative z-10 px-1 ${bold ? 'font-semibold' : ''}`}>
        {value}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Помесячный тренд — упрощённый
// ─────────────────────────────────────────────────────────────────────
function MonthlyTrend({ rows }) {
  const months = ['січ', 'лют', 'бер', 'квіт', 'трав', 'черв', 'лип', 'серп', 'вер', 'жовт', 'лист', 'груд'];
  return (
    <div className="bg-surface rounded-xl shadow border border-border-default p-4">
      <h3 className="font-semibold text-text-primary mb-2">Як змінювалась ціна по місяцях</h3>
      <table className="w-full text-sm">
        <thead className="text-xs text-text-muted uppercase border-b">
          <tr>
            <th className="text-left py-2">Місяць</th>
            <th className="text-right py-2">Декларацій</th>
            <th className="text-right py-2" title="25% сделок нижче цієї ціни">
              Дешевий край, $/кг
            </th>
            <th className="text-right py-2" title="Половина декларацій нижче, половина вище">
              ТИПОВА, $/кг
            </th>
            <th className="text-right py-2" title="25% сделок вище цієї ціни">
              Дорогий край, $/кг
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const d = new Date(r.month);
            const monthLabel = `${months[d.getMonth()]} ${d.getFullYear()}`;
            return (
              <tr key={r.month} className="border-b border-border-default">
                <td className="py-2 font-medium">{monthLabel}</td>
                <td className="text-right">{fmtN(r.n)}</td>
                <td className="text-right text-green-700 dark:text-green-400">{fmtPrice(r.p25)}</td>
                <td className="text-right font-bold text-blue-700 dark:text-blue-300">{fmtPrice(r.price_median)}</td>
                <td className="text-right text-orange-700 dark:text-orange-400">{fmtPrice(r.p75)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Top samples
// ─────────────────────────────────────────────────────────────────────
function SamplesTable({ rows }) {
  return (
    <div className="bg-surface rounded-xl shadow border border-border-default p-4">
      <h3 className="font-semibold text-text-primary mb-2">
        Найбільш схожі товари (топ-{rows.length})
      </h3>
      <p className="text-xs text-text-muted mb-3">
        Це конкретні декларації. Колонка «схожість %» — наскільки опис близький до твого якоря.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-text-muted uppercase border-b">
            <tr>
              <th className="text-left py-2">Схож.</th>
              <th className="text-left py-2">Код</th>
              <th className="text-left py-2">Бренд</th>
              <th className="text-left py-2">Країна</th>
              <th className="text-left py-2">Дата</th>
              <th className="text-center py-2" title="Метод визначення митної вартості">Метод</th>
              <th className="text-right py-2">$/кг</th>
              <th className="text-left py-2 max-w-md">Опис</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.original_id} className="border-b border-border-default hover:bg-hover">
                <td className="py-2">
                  <span className={`font-mono text-xs px-1.5 py-0.5 rounded font-semibold
                    ${r.similarity >= 0.85 ? 'bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300' :
                      r.similarity >= 0.7  ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300' :
                                             'bg-app dark:bg-slate-700 text-text-secondary'}`}>
                    {(r.similarity * 100).toFixed(0)}%
                  </span>
                </td>
                <td><HSCode code={r.product_code} size="sm" /></td>
                <td className="font-semibold">{r.trademark || '—'}</td>
                <td>{r.country || '—'}</td>
                <td className="font-mono text-xs whitespace-nowrap">
                  {r.declaration_date ? (
                    <span className="inline-flex items-center gap-1.5">
                      {r.declaration_date}
                      <SmallYearChip year={r.declaration_year} />
                    </span>
                  ) : '—'}
                </td>
                <td className="text-center">
                  {r.valuation_method != null && <MethodBadge method={r.valuation_method} />}
                </td>
                <td className="text-right font-semibold">{fmtPrice(r.price_per_kg_usd)}</td>
                <td className="max-w-md truncate text-text-secondary" title={r.description}>
                  {r.description?.slice(0, 100)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Drill-панель: розкласти один бар гістограми по поличках
// ─────────────────────────────────────────────────────────────────────
function DrillPanel({ bin, data, loading, error, onClose }) {
  const stats = data?.stats;
  const samples = (data?.samples || []).slice(0, 50);
  const byTM = data?.by_trademark || [];
  const byImp = data?.by_importer || [];
  const byCountry = data?.by_country || [];
  const byCode = data?.by_code || [];
  const byMonth = data?.by_month || [];
  const byValuation = data?.by_valuation_method || [];

  return (
    <div className="bg-surface rounded-xl border-2 border-red-300 dark:border-red-900/60 shadow-md overflow-hidden">
      {/* Header — sticky-feel band з ціновим коридором */}
      <div className="bg-gradient-to-r from-red-50 via-orange-50 to-amber-50 dark:from-red-950/40 dark:via-orange-950/40 dark:to-amber-950/40 border-b border-red-200 dark:border-red-900/60 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-2 h-8 bg-red-500 dark:bg-red-400 rounded-full" />
          <div>
            <div className="text-xs uppercase tracking-wider text-red-700 dark:text-red-300 font-semibold">
              Деталі цінового коридору
            </div>
            <div className="text-base font-bold text-text-primary">
              <span className="font-mono">${bin.range_lo.toFixed(2)}</span>
              <span className="text-text-disabled mx-1">–</span>
              <span className="font-mono">${bin.range_hi.toFixed(2)}</span>
              <span className="text-text-muted font-normal text-sm ml-1">/кг</span>
              {data?.n_with_price != null && (
                <span className="ml-3 text-sm text-text-secondary font-normal">
                  · {fmtN(data.n_with_price)} декларацій
                </span>
              )}
            </div>
          </div>
        </div>
        <button onClick={onClose}
                className="px-3 py-1.5 text-sm font-semibold rounded-lg
                           bg-surface hover:bg-red-100 dark:hover:bg-red-950/40 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-900/60
                           transition-colors flex items-center gap-1.5">
          <X className="w-4 h-4" /> Закрити
        </button>
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        {loading && (
          <div className="flex items-center justify-center py-8 text-text-muted text-sm">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500 dark:border-red-400 mr-3" />
            Збираю деталі коридору…
          </div>
        )}
        {error && (
          <div className="text-red-700 bg-red-50 border border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900/50 rounded p-3 text-sm">
            {error}
          </div>
        )}
        {!loading && !error && data && data.n_with_price === 0 && (
          <div className="text-yellow-800 bg-yellow-50 border border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-900/50 rounded p-3 text-sm">
            У цьому коридорі немає декларацій (фільтри не дали збігу).
          </div>
        )}
        {!loading && !error && data && data.n_with_price > 0 && (
          <>
            {/* Stats summary */}
            {stats && <SimplePriceCard stats={stats} n={data.n_with_price} />}

            {/* Slices grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {byTM.length > 0 && <SliceCard title="За торговою маркою (брендом)" rows={byTM}
                keyCol="trademark" />}
              {byCountry.length > 0 && <SliceCard title="За країною походження" rows={byCountry}
                keyCol="country" />}
              {byImp.length > 0 && <SliceCard title="Хто більше всіх ввозив" rows={byImp.slice(0, 15)}
                keyCol="importer_name"
                keyFmt={(_, row) => <ImporterCell row={row} />}
                extraCol="value_total_usd" extraLabel="Сума, $" />}
              {byCode.length > 0 && <SliceCard title="За кодом УКТЗЕД" rows={byCode}
                keyCol="product_code"
                keyFmt={(v) => <HSCode code={v} size="sm" />} />}
              {byValuation.length > 0 && <ValuationMethodCard rows={byValuation} totalN={data.n_with_price} />}
            </div>

            {/* Monthly */}
            {byMonth.length > 0 && <MonthlyTrend rows={byMonth} />}

            {/* Samples (max 50) */}
            {samples.length > 0 && <SamplesTable rows={samples} />}
          </>
        )}
      </div>
    </div>
  );
}

// Маленький бейдж года для samples
function SmallYearChip({ year }) {
  if (!year) return null;
  const tone = year >= 2024
    ? 'bg-blue-100 text-blue-700 border-blue-200'
    : year >= 2022
      ? 'bg-app dark:bg-slate-700 text-text-secondary border-border-default'
      : 'bg-amber-50 text-amber-700 border-amber-200';
  return (
    <span className={`text-[10px] px-1 py-0 rounded border ${tone}`}>{year}</span>
  );
}
